"use client";

import React, { useRef, useState } from "react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui";

import { useToast } from "@/lib/use-toast";

import { PatientCreate, PatientSummary } from "@/types/patient";

import CreatePatientForm from "./create-patient-form";
import { CreditCardFieldRef } from "./create-patient-form/credit-card-field";
import {
  useCreatePatientMutation,
  useCreatePaymentMethodMutation,
} from "@/api/provider/user/patient/patient-mutation";

interface CreatePatientModalProps {
  onPatientCreate?: (Patient: PatientSummary) => void;
  children?: React.ReactNode;
  className?: string;
  /** Controlled mode: external open state */
  open?: boolean;
  /** Controlled mode: callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Pre-fill values for the form (e.g. firstName/lastName from search) */
  initialData?: Partial<PatientCreate>;
}

export default function CreatePatientModal({
  onPatientCreate,
  children,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  initialData,
}: CreatePatientModalProps) {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const creditCardRef = useRef<CreditCardFieldRef>(null);
  const { toast } = useToast();
  const createPatientMutation = useCreatePatientMutation();
  const createPaymentMethodMutation = useCreatePaymentMethodMutation();

  // Use controlled props if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = isControlled
    ? (open: boolean) => controlledOnOpenChange?.(open)
    : setInternalOpen;

  const handleSubmit = async (patientInfo: PatientCreate): Promise<boolean> => {
    setIsLoading(true);

    let patient: PatientSummary | null;
    try {
      patient = await createPatientMutation.mutateAsync({ ...patientInfo });
    } catch (error) {
      // Catch here rather than letting the rejection escape: it would travel
      // back out through react-hook-form's handleSubmit, skipping the
      // setIsLoading reset below and leaving the button stuck on "Creating…".
      // useCreatePatientMutation's onError already toasts, so don't add a
      // second one — just clear the loading state and stop.
      console.error("Failed to create patient:", error);
      setIsLoading(false);
      return false;
    }

    if (!patient) {
      setIsLoading(false);
      toast({
        title: "Error",
        description: "An error occurred while creating the patient.",
        variant: "error",
      });
      return false;
    }

    // If credit card data was entered (required or optional), submit and save the payment method
    if (creditCardRef.current?.isValid()) {
      const showPaymentWarning = () => {
        toast({
          title: "Patient Created",
          description:
            "Patient created successfully, but failed to save payment method. You can add it later from the patient profile.",
          variant: "warning",
        });
      };

      try {
        const savedCard = await creditCardRef.current.submit(patient.id);
        const paymentMethod = await createPaymentMethodMutation.mutateAsync({
          patientId: patient.id,
          paymentMethodId: savedCard.paymentMethodId,
          preview: savedCard.preview,
        });

        if (!paymentMethod) {
          showPaymentWarning();
        }
      } catch (error) {
        console.error("Failed to save payment method:", error);
        showPaymentWarning();
      }
    }

    setIsLoading(false);
    onPatientCreate?.(patient);
    setIsOpen(false);
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children && (
        <DialogTrigger asChild className={className}>
          {children}
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a patient</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <CreatePatientForm
            onSubmit={handleSubmit}
            isLoading={isLoading}
            creditCardRef={creditCardRef}
            initialData={initialData}
            submitButtonText="Create Patient"
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
